import { LayoutGroup, motion, useReducedMotion } from 'framer-motion'
import { useId } from 'react'

import {
  mergeReducedMotionPreset,
  resolveTabIndicatorTransition,
  useThemeMotion,
} from '../../animation/themeMotion'

export type ThemeLabeledSegmentOption<T extends string> = {
  value: T
  label: string
}

type Props<T extends string> = {
  angular: boolean
  value: T
  options: ThemeLabeledSegmentOption<T>[]
  onChange: (value: T) => void
  'aria-label'?: string
  /** 无外框描边（弹窗内选项卡等） */
  borderless?: boolean
  className?: string
}

/**
 * 文案分段条（滚动/翻页等）：选中态使用 `--segment-active-*`，与 {@link ThemeSegmentRail} 一致。
 */
export function ThemeLabeledSegment<T extends string>({
  angular,
  value,
  options,
  onChange,
  'aria-label': ariaLabel,
  borderless = false,
  className = '',
}: Props<T>) {
  const layoutScope = useId().replaceAll(':', '')
  const preset = useThemeMotion()
  const reducedMotion = useReducedMotion()
  const pillTrans = resolveTabIndicatorTransition(
    mergeReducedMotionPreset(preset, !!reducedMotion),
    { reducedMotion: !!reducedMotion },
  )
  const useSlidePill = !angular

  const railWrap = angular
    ? [
        'theme-segment-compact-rail inline-flex max-h-9 min-h-0 shrink-0 flex-nowrap items-center gap-0',
        borderless
          ? 'rounded-none bg-transparent shadow-none'
          : 'rounded-none border-2 border-line bg-menu-solid shadow-none',
      ].join(' ')
    : borderless
      ? 'inline-flex w-max max-w-none flex-nowrap items-center gap-0.5 rounded-[var(--radius-sm)] bg-menu-muted p-0.5'
      : 'inline-flex w-max max-w-none flex-nowrap items-center gap-0.5 rounded-[var(--radius-sm)] border border-line bg-menu-muted p-0.5'

  const btnClass = (active: boolean) => {
    if (angular) {
      return [
        'relative z-10 min-w-[2.75rem] whitespace-nowrap px-2.5 py-1.5 text-sm font-semibold antialiased transition-colors outline-none focus-visible:ring-2 focus-visible:ring-accent',
        active
          ? 'bg-[var(--segment-active-bg)] text-[var(--segment-active-fg)]'
          : 'text-ink-soft hover:bg-surface-muted hover:text-ink',
      ].join(' ')
    }
    return [
      'relative min-w-[2.75rem] whitespace-nowrap px-2.5 py-1.5 text-sm font-semibold antialiased outline-none focus-visible:ring-2 focus-visible:ring-accent',
      active ? 'text-[var(--segment-active-fg)]' : 'text-ink-soft hover:bg-menu-solid hover:text-ink',
    ].join(' ')
  }

  return (
    <LayoutGroup id={`labeled-seg-${layoutScope}`}>
      <div
        className={[railWrap, className].filter(Boolean).join(' ')}
        role="tablist"
        aria-label={ariaLabel}
      >
        {options.map((opt) => {
          const active = value === opt.value
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              className={btnClass(active)}
              onClick={() => onChange(opt.value)}
            >
              {useSlidePill && active ? (
                <motion.span
                  layoutId={`labeled-seg-pill-${layoutScope}`}
                  className={[
                    'pointer-events-none absolute inset-0 z-0 rounded-[10px] bg-[var(--segment-active-bg)] shadow-sm',
                    borderless ? '' : 'ring-1 ring-[var(--segment-active-ring)]',
                  ].join(' ')}
                  transition={pillTrans}
                />
              ) : null}
              <span className="relative z-10">{opt.label}</span>
            </button>
          )
        })}
      </div>
    </LayoutGroup>
  )
}
